// @flow
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  Hidden,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme
} from '@material-ui/core';
import CssBaseline from '@material-ui/core/CssBaseline';
import { makeStyles } from '@material-ui/core/styles';
import {
  Assignment as SignRequestIcon,
  ExitToApp as SignOutIcon,
  Link as LinkIcon,
  Person as PersonIcon,
  WhatsApp as SupportIcon
} from '@material-ui/icons';
import type { Element, Node } from 'react';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import routes from '../routes';
import { AuthContext } from '../context/auth';
import ApiResponse from '../model/ApiResponse';
import { doGet } from '../util/api';
import { whatsappURL } from '../util/vendor';
import ConfirmationDialog from './ConfirmationDialog';
import Copyright from './Copyright';

const drawerWidth = 240;
const baseUrl = process.env.PUBLIC_URL || '';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  logoContainer: {
    ...theme.mixins.toolbar,
    display: 'flex',
    alignItems: 'center',
  },
  logo: {
    margin: theme.spacing(1),
  },
  drawer: {
    [ theme.breakpoints.up('sm') ]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  appBar: {
    [ theme.breakpoints.up('sm') ]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
  },
  menuButton: {
    marginRight: theme.spacing(2),
    [ theme.breakpoints.up('sm') ]: {
      display: 'none',
    },
  },
  // necessary for content to be below app bar
  toolbar: theme.mixins.toolbar,
  title: {
    flexGrow: 1,
  },
  drawerPaper: {
    width: drawerWidth,
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  signOut: {
    color: theme.palette.error.main,
  },
  support: {
    color: theme.palette.success.main,
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  retailName: {
    color: theme.palette.warning.main,
  },
}));

type Props = {
  activeKey?: string,
  children: Node[] | Element<any>,
};

const Layout = (props: Props) => {
  const { user, setToken } = useContext(AuthContext);
  const { t } = useTranslation();
  const history = useHistory();
  const { activeKey, children } = props;
  const classes = useStyles();
  const theme = useTheme();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [supportPhone, setSupportPhone] = useState<?string>();
  const [agentPhone, setAgentPhone] = useState<?string>();

  useEffect(() => {
    const intervalFetch = async () => {
      let response: ApiResponse = await doGet('/customer/support-phone');
      setSupportPhone(response.data);
      response = await doGet('/customer/agent-phone');
      setAgentPhone(response.data);
    };
    intervalFetch().then();
    const intervalID = setInterval(intervalFetch, 1000 * 60 * 10);
    return () => clearInterval(intervalID);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <div>
      <div className={classes.logoContainer}>
        <img src={`${baseUrl}/images/logo.png`} alt="" width={36} height={36} className={classes.logo}/>
        <Typography variant="h6" display="inline">
          {/* {t('App Name')} */}
        </Typography>
      </div>
      <Divider/>
      <List>
        <ListItem
          button
          selected={activeKey === routes.home}
          disabled={activeKey === routes.home}
          key={routes.home}
          onClick={() => {
            history.push(routes.home);
          }}
        >
          <ListItemIcon>
            <SignRequestIcon/>
          </ListItemIcon>
          <ListItemText primary={t('Sign requests')}/>
        </ListItem>
        <ListItem
          button
          selected={activeKey === routes.profile}
          disabled={activeKey === routes.profile}
          key={routes.profile}
          onClick={() => {
            history.push(routes.profile);
          }}
        >
          <ListItemIcon>
            <PersonIcon/>
          </ListItemIcon>
          <ListItemText primary={t('Profile')}/>
        </ListItem>
        <ListItem
          button
          selected={activeKey === routes.links}
          disabled={activeKey === routes.links}
          key={routes.links}
          onClick={() => {
            history.push(routes.links);
          }}
        >
          <ListItemIcon>
            <LinkIcon className={classes.support}/>
          </ListItemIcon>
          <ListItemText primary={t('Links')} className={classes.support}/>
        </ListItem>
      </List>
      {((supportPhone || agentPhone) && user) && <Divider/>}
      {(agentPhone && user) && <List>
        <ListItem
          button
          key="agentPhone"
          classes={{ button: classes.support }}
          onClick={() => {
            const url = whatsappURL(agentPhone, t('Agent support text', user));
            window.open(url, '_blank');
          }}
        >
          <ListItemIcon>
            <SupportIcon className={classes.support}/>
          </ListItemIcon>
          <ListItemText primary={t('Agent phone')}/>
        </ListItem>
      </List>}
      {(supportPhone && user) && <List>
        <ListItem
          button
          key="supportPhone"
          classes={{ button: classes.support }}
          onClick={() => {
            const url = whatsappURL(supportPhone, t('Support text', user));
            window.open(url, '_blank');
          }}
        >
          <ListItemIcon>
            <SupportIcon className={classes.support}/>
          </ListItemIcon>
          <ListItemText primary={t('Support phone')}/>
        </ListItem>
      </List>}
      <Divider/>
      <List>
        <ListItem
          button
          key="signOut"
          classes={{ button: classes.signOut }}
          onClick={() => setSignOutOpen(true)}
        >
          <ListItemIcon>
            <SignOutIcon className={classes.signOut}/>
          </ListItemIcon>
          <ListItemText primary={t('Sign out')}/>
        </ListItem>
      </List>
      <ConfirmationDialog
        title="Sign out"
        description="Confirm to sign out"
        open={signOutOpen}
        onClose={(ok: boolean) => {
          setSignOutOpen(false);
          if (ok) {
            setToken(null);
          }
        }}
      />
    </div>
  );

  let container = undefined;
  let fcWidget: any = undefined;
  if (typeof(window) === 'function') {
    container = () => window().document.body;
    fcWidget = window()['fcWidget'];
  } else if (typeof(window) === 'object') {
    if (!!window.document) {
      container = () => window.document.body;
    }
    fcWidget = window['fcWidget'];
  }
  if (!!user && !!fcWidget && !fcWidget.doneSetup) {
    fcWidget.setExternalId(user.externalCode || user.code);
    fcWidget.user.setFirstName(user.name);
    fcWidget.user.setEmail(user.email);
    fcWidget.user.setPhone(user.phone);
    fcWidget.doneSetup = true;
    console.log('FreshChat OK!');
  }

  return (
    <div className={classes.root}>
      <CssBaseline/>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            className={classes.menuButton}
          >
            <img src={`${baseUrl}/images/menu.png`} alt="" width={24} height={24}/>
          </IconButton>
          <Typography variant="h6" noWrap className={classes.title}>
            &nbsp;
          </Typography>
          <Button
            disableElevation
            variant="contained"
            color="primary"
            onClick={() => {
              if (activeKey !== routes.profile) {
                history.push(routes.profile);
              }
            }}
          >
            <span className={(!!user && user.isRetail) ? classes.retailName : ''}>
              {user ? user.name : '-'}
            </span>
          </Button>
        </Toolbar>
      </AppBar>
      <nav className={classes.drawer} aria-label="mailbox folders">
        <Hidden smUp>
          <Drawer
            container={container}
            variant="temporary"
            anchor={theme.direction === 'rtl' ? 'right' : 'left'}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            classes={{
              paper: classes.drawerPaper,
            }}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
          >
            {drawer}
          </Drawer>
        </Hidden>
        <Hidden xsDown>
          <Drawer
            classes={{
              paper: classes.drawerPaper,
            }}
            variant="permanent"
            open
          >
            {drawer}
          </Drawer>
        </Hidden>
      </nav>
      <main className={classes.content}>
        <div className={classes.toolbar}/>
        {children}
        <Box mt={5}>
          <Divider className={classes.divider}/>
          <Copyright/>
        </Box>
      </main>
    </div>
  );
};

export default Layout;
